/**
 * Calculate the number of days remaining until a given date
 * @param targetDate - The target date string (YYYY-MM-DD format or ISO date)
 * @returns Number of days remaining (can be negative if date has passed)
 */
export function calculateDaysRemaining(targetDate: string | null): number {
  if (!targetDate) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to midnight

  const target = new Date(targetDate);
  target.setHours(0, 0, 0, 0); // Normalize to midnight

  const diffTime = target.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}

/**
 * Calculate milliseconds until the next midnight
 * Used for scheduling daily refreshes
 * @returns Milliseconds until next midnight
 */
export function getMillisecondsUntilMidnight(): number {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  return tomorrow.getTime() - now.getTime();
}

/**
 * Format the days remaining text for display
 * @param days - Number of days remaining
 * @returns Formatted string for display
 */
export function formatDaysRemaining(days: number): string {
  if (days < 0) return 'Expired';
  if (days === 0) return 'Expires Today';
  if (days === 1) return '1 Day Remaining';
  return `${days} Days Remaining`;
}

/**
 * Calculate and format the 90-day date range based on lock date
 * @param lockedUntil - The lock expiry date string (YYYY-MM-DD format or ISO date)
 * @returns Formatted date range string (e.g., "Jan 12 - Apr 12, 2026")
 */
export function format90DayRange(lockedUntil: string | null): string {
  if (!lockedUntil) return '90-day Strategy Period';

  const endDate = new Date(lockedUntil);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - 90);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const startMonth = monthNames[startDate.getMonth()];
  const startDay = startDate.getDate();
  const endMonth = monthNames[endDate.getMonth()];
  const endDay = endDate.getDate();
  const year = endDate.getFullYear();

  return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
}

/**
 * Format the unlock date for display
 * @param lockedUntil - The lock expiry date string (YYYY-MM-DD format or ISO date)
 * @returns Formatted unlock date string (e.g., "Unlocks on Apr 12, 2026")
 */
export function formatUnlockDate(lockedUntil: string | null): string {
  if (!lockedUntil) return 'Unlock date not set';
  
  const unlockDate = new Date(lockedUntil);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const month = monthNames[unlockDate.getMonth()];
  const day = unlockDate.getDate();
  const year = unlockDate.getFullYear();
  
  return `Unlocks on ${month} ${day}, ${year}`;
}
